/**
 * ComposerIcons — the real SVG icon set for the composer's controls (lane
 * va-ui-composer-fsm-2026-10-01, issue #2204).
 *
 * The issue's no-emoji-as-icon law for the composer surfaces: every control renders an
 * unambiguous stroke icon in the hero mic's exact visual idiom (24×24 viewBox, currentColor
 * strokes, round caps — see DesktopHome's hero svg). Pure presentational components: no
 * state, no behavior. Shared by the firewall-neutral InputBar/PTTButton and the desktop
 * RolePicker (components/ is the firewall-neutral bucket both shells already import).
 */

interface IconProps {
  /** Additional classes (e.g. sizing hooks). */
  class?: string;
}

const SVG_PROPS = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  'stroke-width': 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

/** The push-to-talk / dictate mic — the same glyph the home hero renders. */
export function MicIcon({ class: cls }: IconProps) {
  return (
    <svg class={cls} {...SVG_PROPS}>
      <path d="M12 3a3 3 0 0 1 3 3v5a3 3 0 0 1-6 0V6a3 3 0 0 1 3-3Z" />
      <path d="M5.5 11a6.5 6.5 0 0 0 13 0" />
      <path d="M12 17.5V21" />
      <path d="M8.5 21h7" />
    </svg>
  );
}

/** Mic with a slash — the permission-denied / blocked variant of the dictate control. */
export function MicBlockedIcon({ class: cls }: IconProps) {
  return (
    <svg class={cls} {...SVG_PROPS}>
      <path d="M12 3a3 3 0 0 1 3 3v5a3 3 0 0 1-6 0V6a3 3 0 0 1 3-3Z" />
      <path d="M5.5 11a6.5 6.5 0 0 0 13 0" />
      <path d="M12 17.5V21" />
      <path d="M8.5 21h7" />
      <path d="M4 4l16 16" />
    </svg>
  );
}

/** Paperclip — the attach control. */
export function AttachIcon({ class: cls }: IconProps) {
  return (
    <svg class={cls} {...SVG_PROPS}>
      <path d="m21.4 11.05-8.5 8.5a5.5 5.5 0 0 1-7.78-7.78l8.49-8.49a3.67 3.67 0 0 1 5.19 5.19l-8.49 8.48a1.83 1.83 0 0 1-2.59-2.59l7.78-7.78" />
    </svg>
  );
}

/** Refresh / catch-up — the context-refresh control. */
export function CatchupIcon({ class: cls }: IconProps) {
  return (
    <svg class={cls} {...SVG_PROPS}>
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <path d="M21 3v6h-6" />
    </svg>
  );
}

/** Send arrow — the send control. */
export function SendIcon({ class: cls }: IconProps) {
  return (
    <svg class={cls} {...SVG_PROPS}>
      <path d="M4.5 12 20 4.5 14 20l-2.6-6.2L4.5 12Z" />
      <path d="M11.4 13.8 20 4.5" />
    </svg>
  );
}

/** Dismiss cross — the cancel control (busy swap / recording cancel). */
export function CancelIcon({ class: cls }: IconProps) {
  return (
    <svg class={cls} {...SVG_PROPS}>
      <path d="M6 6l12 12" />
      <path d="M18 6 6 18" />
    </svg>
  );
}

/** Stop square — the recording-dock stop control. */
export function StopIcon({ class: cls }: IconProps) {
  return (
    <svg class={cls} {...SVG_PROPS}>
      <rect x="6.5" y="6.5" width="11" height="11" rx="1.5" />
    </svg>
  );
}

/** The Role chip glyph — two persona silhouettes (the "mode" affordance). */
export function RoleIcon({ class: cls }: IconProps) {
  return (
    <svg class={cls} {...SVG_PROPS}>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 19.5a5.5 5.5 0 0 1 11 0" />
      <path d="M15.5 5.4a3.2 3.2 0 0 1 0 5.5" />
      <path d="M17 14.4a5.5 5.5 0 0 1 3.5 5.1" />
    </svg>
  );
}

/* ── Mobile tab-bar icons (lane va-ui-mobile-interior-off-tokens-2026-10-01, #2216) ──
 * The bottom nav's six destinations in the same stroke idiom as the icons above — one icon
 * system for both shells, replacing the emoji / text-glyph slot icons. */

/** Agents — two people (the session list). */
export function AgentsIcon({ class: cls }: IconProps) {
  return (
    <svg class={cls} {...SVG_PROPS}>
      <circle cx="9" cy="8" r="3" />
      <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
      <path d="M16 5.2a3 3 0 0 1 0 5.6" />
      <path d="M17.5 14a5.5 5.5 0 0 1 3 5" />
    </svg>
  );
}

/** Tasks — a checked circle. */
export function TasksIcon({ class: cls }: IconProps) {
  return (
    <svg class={cls} {...SVG_PROPS}>
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12.5l2.7 2.7L16 9.5" />
    </svg>
  );
}

/** Files — a folder. */
export function FilesIcon({ class: cls }: IconProps) {
  return (
    <svg class={cls} {...SVG_PROPS}>
      <path d="M3 7.5A1.5 1.5 0 0 1 4.5 6H9l2 2.5h8.5A1.5 1.5 0 0 1 21 10v8.5a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 18.5Z" />
    </svg>
  );
}

/** Ops — a wrench. */
export function OpsIcon({ class: cls }: IconProps) {
  return (
    <svg class={cls} {...SVG_PROPS}>
      <path d="M14.5 5.5a4 4 0 0 0 4.7 5.2l-9.2 9.2a2 2 0 0 1-2.9-2.9l9.2-9.2a4 4 0 0 0-1.8-2.3Z" />
    </svg>
  );
}

/** Settings — two sliders. */
export function SettingsIcon({ class: cls }: IconProps) {
  return (
    <svg class={cls} {...SVG_PROPS}>
      <path d="M4 7h9M17 7h3M4 17h3M11 17h9" />
      <circle cx="15" cy="7" r="2" />
      <circle cx="9" cy="17" r="2" />
    </svg>
  );
}

/** More — three dots. */
export function MoreIcon({ class: cls }: IconProps) {
  return (
    <svg class={cls} {...SVG_PROPS}>
      <circle cx="5.5" cy="12" r="1.2" fill="currentColor" />
      <circle cx="12" cy="12" r="1.2" fill="currentColor" />
      <circle cx="18.5" cy="12" r="1.2" fill="currentColor" />
    </svg>
  );
}
